import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import keycode from 'keycode';
import { testable } from '@instructure/ui-testable';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { getElementType, getInteraction, passthroughProps, callRenderProp } from '@instructure/ui-react-utils';
import { isActiveElement } from '@instructure/ui-dom-utils';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
import { View } from '@instructure/ui-view';
import { Flex } from '@instructure/ui-flex';
var styles = {
  componentId: 'fQfxa',
  template: function template(theme) {
    return "\n\n.fQfxa_caGd{-ms-user-select:none;-webkit-user-select:none;border-radius:".concat(theme.borderRadius || 'inherit', ";border-style:").concat(theme.borderStyle || 'inherit', ";border-width:").concat(theme.borderWidth || 'inherit', ";box-sizing:border-box;direction:inherit;display:block;font-family:").concat(theme.fontFamily || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";letter-spacing:").concat(theme.letterSpacing || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";text-transform:").concat(theme.textTransform || 'inherit', ";transform:").concat(theme.transform || 'inherit', ";transition:background 0.2s,transform 0.2s;user-select:none;width:100%}\n\n.fQfxa_caGd:hover{transform:").concat(theme.hoverTransform || 'inherit', "}\n\n.fQfxa_caGd.fQfxa_EMjX{text-align:start}\n\n[dir=ltr] .fQfxa_caGd.fQfxa_EMjX{text-align:left}\n\n[dir=rtl] .fQfxa_caGd.fQfxa_EMjX{text-align:right}\n\n.fQfxa_caGd.fQfxa_ImeN,[dir=ltr] .fQfxa_caGd.fQfxa_ImeN,[dir=rtl] .fQfxa_caGd.fQfxa_ImeN{text-align:center}\n\n.fQfxa_caGd.fQfxa_nWmp{padding-left:0;padding-right:0}\n\n.fQfxa_caGd.fQfxa_nWmp .fQfxa_biBD{padding-bottom:0;padding-top:0}\n\n.fQfxa_bZuE{opacity:0.5}\n\n.fQfxa_VCXp{font-size:").concat(theme.smallFontSize || 'inherit', ";padding-left:").concat(theme.smallPaddingHorizontal || 'inherit', ";padding-right:").concat(theme.smallPaddingHorizontal || 'inherit', "}\n\n.fQfxa_VCXp .fQfxa_biBD{padding-bottom:").concat(theme.smallPaddingBottom || 'inherit', ";padding-top:").concat(theme.smallPaddingTop || 'inherit', "}\n\n.fQfxa_VCXp .fQfxa_eoCh{font-size:").concat(theme.iconSizeSmall || 'inherit', "}\n\n.fQfxa_VCXp.fQfxa_bIHL{height:").concat(theme.smallHeight || 'inherit', ";padding-left:0;padding-right:0;width:").concat(theme.smallHeight || 'inherit', "}\n\n.fQfxa_VCXp.fQfxa_nWmp .fQfxa_eoCh{font-size:").concat(theme.smallFontSize || 'inherit', "}\n\n.fQfxa_fKcQ{font-size:").concat(theme.mediumFontSize || 'inherit', ";padding-left:").concat(theme.mediumPaddingHorizontal || 'inherit', ";padding-right:").concat(theme.mediumPaddingHorizontal || 'inherit', "}\n\n.fQfxa_fKcQ .fQfxa_biBD{padding-bottom:").concat(theme.mediumPaddingBottom || 'inherit', ";padding-top:").concat(theme.mediumPaddingTop || 'inherit', "}\n\n.fQfxa_fKcQ .fQfxa_eoCh{font-size:").concat(theme.iconSizeMedium || 'inherit', "}\n\n.fQfxa_fKcQ.fQfxa_bIHL{height:").concat(theme.mediumHeight || 'inherit', ";padding-left:0;padding-right:0;width:").concat(theme.mediumHeight || 'inherit', "}\n\n.fQfxa_fKcQ.fQfxa_nWmp .fQfxa_eoCh{font-size:").concat(theme.mediumFontSize || 'inherit', "}\n\n.fQfxa_cnhd{font-size:").concat(theme.largeFontSize || 'inherit', ";padding-left:").concat(theme.largePaddingHorizontal || 'inherit', ";padding-right:").concat(theme.largePaddingHorizontal || 'inherit', "}\n\n.fQfxa_cnhd .fQfxa_biBD{padding-bottom:").concat(theme.largePaddingBottom || 'inherit', ";padding-top:").concat(theme.largePaddingTop || 'inherit', "}\n\n.fQfxa_cnhd .fQfxa_eoCh{font-size:").concat(theme.iconSizeLarge || 'inherit', "}\n\n.fQfxa_cnhd.fQfxa_bIHL{height:").concat(theme.largeHeight || 'inherit', ";padding-left:0;padding-right:0;width:").concat(theme.largeHeight || 'inherit', "}\n\n.fQfxa_cnhd.fQfxa_nWmp .fQfxa_eoCh{font-size:").concat(theme.largeFontSize || 'inherit', "}\n\n.fQfxa_eoCh{align-items:center;display:flex}\n\n.fQfxa_biBD{display:block}\n\n.fQfxa_bIHL{line-height:1}\n\n.fQfxa_FJpd{border-radius:50%}\n\n.fQfxa_dqAF.fQfxa_eCSh{background:").concat(theme.primaryBackground || 'inherit', ";border-color:").concat(theme.primaryBorderColor || 'inherit', ";color:").concat(theme.primaryColor || 'inherit', "}\n\n.fQfxa_dqAF.fQfxa_buuG{background:").concat(theme.secondaryBackground || 'inherit', ";border-color:").concat(theme.secondaryBorderColor || 'inherit', ";color:").concat(theme.secondaryColor || 'inherit', "}\n\n.fQfxa_dqAF.fQfxa_bFtJ{background:").concat(theme.primaryInverseBackground || 'inherit', ";border-color:").concat(theme.primaryInverseBorderColor || 'inherit', ";color:").concat(theme.primaryInverseColor || 'inherit', "}\n\n.fQfxa_dqAF.fQfxa_eZal{background:").concat(theme.successBackground || 'inherit', ";border-color:").concat(theme.successBorderColor || 'inherit', ";color:").concat(theme.successColor || 'inherit', "}\n\n.fQfxa_dqAF.fQfxa_dRSL{background:").concat(theme.dangerBackground || 'inherit', ";border-color:").concat(theme.dangerBorderColor || 'inherit', ";color:").concat(theme.dangerColor || 'inherit', "}\n\n.fQfxa_bCUx.fQfxa_eCSh{background:").concat(theme.primaryGhostBackground || 'inherit', ";border-color:").concat(theme.primaryGhostBorderColor || 'inherit', ";color:").concat(theme.primaryGhostColor || 'inherit', "}\n\n.fQfxa_bCUx.fQfxa_buuG{background:").concat(theme.secondaryGhostBackground || 'inherit', ";border-color:").concat(theme.secondaryGhostBorderColor || 'inherit', ";color:").concat(theme.secondaryGhostColor || 'inherit', "}\n\n.fQfxa_bCUx.fQfxa_bFtJ{background:").concat(theme.primaryInverseGhostBackground || 'inherit', ";border-color:").concat(theme.primaryInverseGhostBorderColor || 'inherit', ";color:").concat(theme.primaryInverseGhostColor || 'inherit', "}\n\n.fQfxa_bCUx.fQfxa_eZal{background:").concat(theme.successGhostBackground || 'inherit', ";border-color:").concat(theme.successGhostBorderColor || 'inherit', ";color:").concat(theme.successGhostColor || 'inherit', "}\n\n.fQfxa_bCUx.fQfxa_dRSL{background:").concat(theme.dangerGhostBackground || 'inherit', ";border-color:").concat(theme.dangerGhostBorderColor || 'inherit', ";color:").concat(theme.dangerGhostColor || 'inherit', "}\n\n.fQfxa_bVmg{border-style:none}\n\n.fQfxa_bGBk{-moz-appearance:none;-webkit-appearance:none;appearance:none;text-decoration:none;touch-action:manipulation}\n\n.fQfxa_bGBk::-moz-focus-inner{border:0}\n\n.fQfxa_bGBk *{pointer-events:none}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_dqAF.fQfxa_eCSh{background:").concat(theme.primaryActiveBackground || 'inherit', ";box-shadow:").concat(theme.primaryActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_dqAF.fQfxa_buuG{background:").concat(theme.secondaryActiveBackground || 'inherit', ";box-shadow:").concat(theme.secondaryActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_dqAF.fQfxa_bFtJ{background:").concat(theme.primaryInverseActiveBackground || 'inherit', ";box-shadow:").concat(theme.primaryInverseActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_dqAF.fQfxa_eZal{background:").concat(theme.successActiveBackground || 'inherit', ";box-shadow:").concat(theme.successActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_dqAF.fQfxa_dRSL{background:").concat(theme.dangerActiveBackground || 'inherit', ";box-shadow:").concat(theme.dangerActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_bCUx.fQfxa_eCSh{background:").concat(theme.primaryGhostActiveBackground || 'inherit', ";box-shadow:").concat(theme.primaryGhostActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_bCUx.fQfxa_buuG{background:").concat(theme.secondaryGhostActiveBackground || 'inherit', ";box-shadow:").concat(theme.secondaryGhostActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_bCUx.fQfxa_bFtJ{background:").concat(theme.primaryInverseGhostActiveBackground || 'inherit', ";box-shadow:").concat(theme.primaryInverseGhostActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_bCUx.fQfxa_eZal{background:").concat(theme.successGhostActiveBackground || 'inherit', ";box-shadow:").concat(theme.successGhostActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:active .fQfxa_caGd.fQfxa_bCUx.fQfxa_dRSL{background:").concat(theme.dangerGhostActiveBackground || 'inherit', ";box-shadow:").concat(theme.dangerGhostActiveBoxShadow || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_dqAF.fQfxa_eCSh{background:").concat(theme.primaryHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_dqAF.fQfxa_buuG{background:").concat(theme.secondaryHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_dqAF.fQfxa_bFtJ{background:").concat(theme.primaryInverseHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_dqAF.fQfxa_eZal{background:").concat(theme.successHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_dqAF.fQfxa_dRSL{background:").concat(theme.dangerHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_bCUx.fQfxa_eCSh{background:").concat(theme.primaryGhostHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_bCUx.fQfxa_buuG{background:").concat(theme.secondaryGhostHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_bCUx.fQfxa_bFtJ{background:").concat(theme.primaryInverseGhostHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_bCUx.fQfxa_eZal{background:").concat(theme.successGhostHoverBackground || 'inherit', "}\n\n.fQfxa_bGBk:hover .fQfxa_bCUx.fQfxa_dRSL{background:").concat(theme.dangerGhostHoverBackground || 'inherit', "}");
  },
  'content': 'fQfxa_caGd',
  'textAlign--start': 'fQfxa_EMjX',
  'textAlign--center': 'fQfxa_ImeN',
  'isCondensed': 'fQfxa_nWmp',
  'children': 'fQfxa_biBD',
  'isDisabled': 'fQfxa_bZuE',
  'size--small': 'fQfxa_VCXp',
  'iconSVG': 'fQfxa_eoCh',
  'hasOnlyIconVisible': 'fQfxa_bIHL',
  'size--medium': 'fQfxa_fKcQ',
  'size--large': 'fQfxa_cnhd',
  'shape--circle': 'fQfxa_FJpd',
  'withBackground': 'fQfxa_dqAF',
  'color--primary': 'fQfxa_eCSh',
  'color--secondary': 'fQfxa_buuG',
  'color--primary-inverse': 'fQfxa_bFtJ',
  'color--success': 'fQfxa_eZal',
  'color--danger': 'fQfxa_dRSL',
  'withoutBackground': 'fQfxa_bCUx',
  'withoutBorder': 'fQfxa_bVmg',
  'root': 'fQfxa_bGBk'
};
import theme from './theme';
/**
---
category: components/utilities
---
**/

var BaseButton = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(BaseButton, _Component);

  var _super = _createSuper(BaseButton);

  function BaseButton() {
    var _this;

    _classCallCheck(this, BaseButton);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));
    _this._rootElement = null;

    _this.handleElementRef = function (el) {
      _this._rootElement = el;

      _this.props.elementRef(el);
    };

    _this.handleClick = function (event) {
      var onClick = _this.props.onClick;

      var _assertThisInitialize = _assertThisInitialized(_this),
          interaction = _assertThisInitialize.interaction;

      if (interaction !== 'enabled') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (typeof onClick === 'function') {
        onClick(event);
      }
    };

    _this.handleKeyDown = function (event) {
      var _this$props = _this.props,
          onClick = _this$props.onClick,
          onKeyDown = _this$props.onKeyDown,
          href = _this$props.href;

      var _assertThisInitialize2 = _assertThisInitialized(_this),
          interaction = _assertThisInitialize2.interaction;

      onKeyDown(event); // behave like a button when space key is pressed

      var _keycode$codes = keycode.codes,
          space = _keycode$codes.space,
          enter = _keycode$codes.enter;

      if (_this.elementType !== 'button' && [space, enter].includes(event.keyCode)) {
        event.preventDefault();
        event.stopPropagation();

        if (typeof onClick === 'function' && interaction === 'enabled') {
          onClick(event);
        }

        if (href) {
          _this._rootElement && _this._rootElement.click();
        }
      }
    };

    return _this;
  }

  _createClass(BaseButton, [{
    key: "focus",
    value: function focus() {
      this._rootElement && this._rootElement.focus();
    }
  }, {
    key: "renderChildren",
    value: function renderChildren() {
      var _this$props2 = this.props,
          renderIcon = _this$props2.renderIcon,
          children = _this$props2.children,
          textAlign = _this$props2.textAlign,
          isCondensed = _this$props2.isCondensed;
      var wrappedChildren = /*#__PURE__*/React.createElement("span", {
        className: styles.children
      }, children);

      if (!renderIcon) {
        return wrappedChildren;
      }

      var hasOnlyIconVisible = this.hasOnlyIconVisible;
      var wrappedIcon = /*#__PURE__*/React.createElement("span", {
        className: styles.iconSVG
      }, callRenderProp(renderIcon));
      var flexChildren = hasOnlyIconVisible ? /*#__PURE__*/React.createElement(Flex.Item, null, wrappedIcon, children) : [/*#__PURE__*/React.createElement(Flex.Item, {
        key: "icon",
        padding: "0 ".concat(isCondensed ? 'xx-small' : 'x-small', " 0 0")
      }, wrappedIcon), /*#__PURE__*/React.createElement(Flex.Item, {
        key: "children",
        shouldShrink: true
      }, wrappedChildren)];
      var flexProps = {
        shouldShrink: true,
        height: '100%',
        width: '100%',
        justifyItems: hasOnlyIconVisible || textAlign === 'center' ? 'center' : 'start'
      };
      return /*#__PURE__*/React.createElement(Flex, flexProps, flexChildren);
    }
  }, {
    key: "render",
    value: function render() {
      var _classnames;

      var _this$props3 = this.props,
          type = _this$props3.type,
          size = _this$props3.size,
          elementRef = _this$props3.elementRef,
          as = _this$props3.as,
          href = _this$props3.href,
          color = _this$props3.color,
          focusColor = _this$props3.focusColor,
          textAlign = _this$props3.textAlign,
          shape = _this$props3.shape,
          display = _this$props3.display,
          withBackground = _this$props3.withBackground,
          withBorder = _this$props3.withBorder,
          isCondensed = _this$props3.isCondensed,
          margin = _this$props3.margin,
          cursor = _this$props3.cursor,
          onClick = _this$props3.onClick,
          renderIcon = _this$props3.renderIcon,
          tabIndex = _this$props3.tabIndex,
          props = _objectWithoutProperties(_this$props3, ["type", "size", "elementRef", "as", "href", "color", "focusColor", "textAlign", "shape", "display", "withBackground", "withBorder", "isCondensed", "margin", "cursor", "onClick", "renderIcon", "tabIndex"]);

      var interaction = this.interaction;
      var isDisabled = interaction === 'disabled';
      var isReadOnly = interaction === 'readonly';
      var isEnabled = interaction === 'enabled';
      var hasOnlyIconVisible = this.hasOnlyIconVisible;
      var classes = classnames((_classnames = {}, _defineProperty(_classnames, styles.content, true), _defineProperty(_classnames, styles["size--".concat(size)], true), _defineProperty(_classnames, styles["color--".concat(color)], true), _defineProperty(_classnames, styles["textAlign--".concat(textAlign)], true), _defineProperty(_classnames, styles["shape--".concat(shape)], true), _defineProperty(_classnames, styles.withBackground, withBackground), _defineProperty(_classnames, styles.withoutBackground, !withBackground), _defineProperty(_classnames, styles.isCondensed, isCondensed), _defineProperty(_classnames, styles.withBorder, withBorder), _defineProperty(_classnames, styles.withoutBorder, !withBorder), _defineProperty(_classnames, styles.hasOnlyIconVisible, hasOnlyIconVisible), _defineProperty(_classnames, styles.isDisabled, isDisabled), _classnames));
      return /*#__PURE__*/React.createElement(View, Object.assign({}, passthroughProps(props), {
        as: this.elementType,
        focusColor: this.focusColor,
        position: "relative",
        display: display,
        width: display === 'block' ? '100%' : 'auto',
        borderRadius: shape === 'circle' ? 'circle' : 'medium',
        background: "transparent",
        padding: "none",
        borderWidth: "none",
        margin: margin,
        cursor: isDisabled ? 'not-allowed' : cursor,
        href: href,
        type: href ? null : type,
        elementRef: this.handleElementRef,
        onClick: this.handleClick,
        onKeyDown: this.handleKeyDown,
        role: onClick && as !== 'button' ? 'button' : null,
        tabIndex: onClick && as ? tabIndex || '0' : tabIndex,
        disabled: isDisabled || isReadOnly // TODO: Eventually remove classname. That will involve figuring out where the button reset should live, as well
        // as creating a selector for the active and hover states
        ,
        className: isEnabled ? styles.root : null
      }), /*#__PURE__*/React.createElement("span", {
        className: classes
      }, this.renderChildren()));
    }
  }, {
    key: "hasOnlyIconVisible",
    get: function get() {
      var _this$props4 = this.props,
          children = _this$props4.children,
          renderIcon = _this$props4.renderIcon;
      return renderIcon && !hasVisibleChildren(children);
    }
  }, {
    key: "elementType",
    get: function get() {
      return getElementType(BaseButton, this.props);
    }
  }, {
    key: "interaction",
    get: function get() {
      return getInteraction({
        props: this.props
      });
    }
  }, {
    key: "focusColor",
    get: function get() {
      var _this$props5 = this.props,
          color = _this$props5.color,
          focusColor = _this$props5.focusColor,
          withBackground = _this$props5.withBackground; // Give user specified focusColor preference

      if (focusColor) {
        return focusColor;
      } // The `primary-inverse` background has an info focus outline
      // by default since it is replacing the `light` button variant.
      // Override the focus color with info even though it is
      // an inverse color


      if (color === 'primary-inverse' && withBackground) {
        return 'info';
      }

      return color.includes('inverse') ? 'inverse' : 'info';
    }
  }, {
    key: "focused",
    get: function get() {
      return isActiveElement(this._rootElement);
    }
  }]);

  BaseButton.displayName = "BaseButton";
  return BaseButton;
}(Component), _class2.propTypes = {
  /**
   * Specifies the `Button` children.
   */
  children: PropTypes.node,

  /**
   * Specifies the type of the `Button`'s underlying html element.
   */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),

  /**
   * The size of the `Button`
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
   * Provides a reference to the `Button`'s underlying html element.
   */
  elementRef: PropTypes.func,

  /**
   * The element to render as the component root, `Button` by default.
   */
  as: PropTypes.elementType,

  /**
   * Specifies if interaction with the `Button` is enabled, disabled, or readonly.
   */
  interaction: PropTypes.oneOf(['enabled', 'disabled', 'readonly']),

  /**
   * Specifies the color for the `Button`.
   */
  color: PropTypes.oneOf(['primary', 'primary-inverse', 'secondary', 'success', 'danger']),

  /**
   * Override the `Button`'s default focus outline color.
   */
  focusColor: PropTypes.oneOf(['info', 'inverse']),

  /**
   * The `Button` display property. When set to `inline-block`, the `Button` displays inline with other elements.
   * When set to block, the `Button` expands to fill the width of the container.
   */
  display: PropTypes.oneOf(['inline-block', 'block']),

  /**
   * Sets the alignment of the `Button` children and/or icon.
   */
  textAlign: PropTypes.oneOf(['start', 'center']),

  /**
   * Specifies if the `Button` shape should be a circle or rectangle.
   */
  shape: PropTypes.oneOf(['rectangle', 'circle']),

  /**
   * Specifies if the `Button` should render with a solid background. When false, the background is transparent.
   */
  withBackground: PropTypes.bool,

  /**
   * Specifies if the `Button` should render with a border.
   */
  withBorder: PropTypes.bool,

  /**
   * Designates if the `Button` should render without padding. This option should only be set when `withBorder` and
   * `withBackground` are also set to false.
   */
  isCondensed: PropTypes.bool,

  /**
   * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
   * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
   * familiar CSS-like shorthand. For example: `margin="small auto large"`.
   */
  margin: ThemeablePropTypes.spacing,

  /**
   * Specify a mouse cursor to use when hovering over the button.
   * The `pointer` cursor is used by default.
   */
  cursor: PropTypes.string,

  /**
   * Specifies an href attribute for the `Button`'s underlying html element.
   */
  href: PropTypes.string,

  /**
   * Callback fired when the `Button` is clicked.
   */
  onClick: PropTypes.func,

  /**
   * Callback fired when the `Button` receives a keydown event.
   */
  onKeyDown: PropTypes.func,

  /**
   * An icon, or function that returns an icon.
   */
  renderIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * Specifies the tabindex of the `Button`.
   */
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}, _class2.defaultProps = {
  children: null,
  type: 'button',
  size: 'medium',
  elementRef: function elementRef(el) {},
  as: 'button',
  // Leave interaction default undefined so that `disabled` and `readOnly` can also be supplied
  interaction: void 0,
  color: 'secondary',
  focusColor: void 0,
  shape: 'rectangle',
  display: 'inline-block',
  textAlign: 'start',
  withBackground: true,
  withBorder: true,
  isCondensed: false,
  margin: '0',
  cursor: 'pointer',
  href: void 0,
  onClick: void 0,
  onKeyDown: function onKeyDown(event) {},
  renderIcon: void 0,
  tabIndex: void 0
}, _temp)) || _class) || _class);
export { BaseButton };
export default BaseButton;